import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, MessageCircle, CheckCircle2, XCircle } from "lucide-react";
import { IntegrationApiLogToggle } from "./IntegrationApiLogToggle";
import CopyButton from "@/components/admin/CopyButton";

const API = getApiUrl();

export default function WhatsAppIntegrationTab() {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    access_token: "",
    message: "Hello from Sell Local Homes — this is a test WhatsApp message.",
    recipient_number: "",
    message_type: "template",
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/whatsapp/config`, { withCredentials: true });
        setForm((prev) => ({
          ...prev,
          access_token: r.data?.access_token || "",
          recipient_number: r.data?.default_recipient || "",
        }));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleTest = async () => {
    if (!form.access_token.trim() || !form.recipient_number.trim()) {
      toast.error("Access token and recipient number are required");
      return;
    }
    if (form.message_type === "text" && !form.message.trim()) {
      toast.error("Message is required for free-form text");
      return;
    }
    try {
      setSending(true);
      setResult(null);
      const r = await axios.post(`${API}/admin/whatsapp/test`, form, { withCredentials: true });
      setResult({ success: true, detail: JSON.stringify(r.data?.response || {}, null, 2) });
      toast.success("WhatsApp test message sent");
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed to send WhatsApp message";
      setResult({ success: false, detail });
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="space-y-3" data-testid="whatsapp-integration-tab">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-emerald-600" />
          WhatsApp
        </h3>
        <p className="text-sm text-muted-foreground">
          Send a test message via the Meta WhatsApp Cloud API to confirm the integration is working.
        </p>
      </div>
      <IntegrationApiLogToggle provider="whatsapp" />
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Send Test Message</CardTitle>
          <CardDescription>Uses Graph API v25.0 against your configured phone number ID.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="wa-token">Access Token *</Label>
                <Input
                  id="wa-token"
                  data-testid="whatsapp-access-token-input"
                  type="text"
                  value={form.access_token}
                  onChange={(e) => setForm({ ...form, access_token: e.target.value })}
                  placeholder="EAAU..."
                  className="font-mono text-xs"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wa-recipient">Recipient Number *</Label>
                <Input
                  id="wa-recipient"
                  data-testid="whatsapp-recipient-input"
                  value={form.recipient_number}
                  onChange={(e) => setForm({ ...form, recipient_number: e.target.value })}
                  placeholder="+447850007215"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wa-type">Message Type *</Label>
                <Select
                  value={form.message_type}
                  onValueChange={(v) => setForm({ ...form, message_type: v })}
                >
                  <SelectTrigger id="wa-type" data-testid="whatsapp-message-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="template">Approved Template (hello_world) — delivers instantly</SelectItem>
                    <SelectItem value="text">Free-form Text — requires recipient to have messaged first</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {form.message_type === "template"
                    ? "Meta only allows free text once the recipient has messaged your business number within the last 24 hours. Use this pre-approved template for the first test — it always delivers."
                    : "Only delivers if the recipient has sent your WhatsApp number a message within the last 24 hours. Have them message you once, then this will work."}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="wa-message">Message {form.message_type === "text" && "*"}</Label>
                <Textarea
                  id="wa-message"
                  data-testid="whatsapp-message-input"
                  rows={3}
                  value={form.message}
                  disabled={form.message_type === "template"}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder={form.message_type === "template" ? "Template text is fixed by Meta (hello_world)" : ""}
                />
              </div>
              <Button
                onClick={handleTest}
                disabled={sending}
                data-testid="whatsapp-send-test-btn"
              >
                {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send Test Message
              </Button>

              {result && (
                <div
                  data-testid="whatsapp-test-result"
                  className={`rounded-md border p-3 text-sm flex items-start gap-2 ${
                    result.success
                      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                      : "bg-red-50 border-red-200 text-red-900"
                  }`}
                >
                  {result.success ? (
                    <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  )}
                  <pre className="whitespace-pre-wrap break-all font-mono text-xs flex-1">{result.detail}</pre>
                  <CopyButton text={result.detail} testId="whatsapp-test-result-copy" />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
